import type { ProgramPublicProfile } from '@/mocks/programProfile';

interface Props {
  profile: ProgramPublicProfile;
}

export default function ProgramTopFansSection({ profile }: Props) {
  const podium = profile.topFans.slice(0, 3);
  const rest = profile.topFans.slice(3);

  return (
    <section className="bg-background-50 rounded-2xl border border-background-200/70 p-5 md:p-6">
      <div className="flex items-center gap-2 mb-6">
        <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
          <i className="ri-trophy-line text-accent-600 text-sm"></i>
        </div>
        <h2 className="text-base font-bold text-foreground-950">Top Fans</h2>
        <span className="text-xs text-foreground-400 ml-auto">Top {profile.topFans.length}</span>
      </div>

      {/* Podium */}
      <div className="flex items-end justify-center gap-3 md:gap-5 mb-8">
        {/* 2nd */}
        {podium[1] && (
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full border-3 border-gray-300 overflow-hidden mb-2">
              <img src={podium[1].photoUrl} alt={podium[1].name} className="w-full h-full object-cover" />
            </div>
            <div className="w-20 md:w-24 h-20 md:h-24 bg-gray-200 rounded-t-xl flex flex-col items-center justify-center">
              <span className="text-lg md:text-xl font-bold text-gray-500">2</span>
              <span className="text-[9px] md:text-[10px] text-gray-400">{podium[1].score}</span>
            </div>
            <p className="text-[10px] md:text-xs font-semibold text-foreground-800 mt-1.5 text-center">{podium[1].name.split(' ')[0]}</p>
          </div>
        )}

        {/* 1st */}
        {podium[0] && (
          <div className="flex flex-col items-center">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-full border-3 border-amber-400 overflow-hidden mb-2 relative">
              <img src={podium[0].photoUrl} alt={podium[0].name} className="w-full h-full object-cover" />
              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400 flex items-center justify-center">
                <i className="ri-vip-crown-fill text-white text-[10px]"></i>
              </div>
            </div>
            <div className="w-24 md:w-28 h-28 md:h-32 bg-amber-100 rounded-t-xl flex flex-col items-center justify-center">
              <span className="text-xl md:text-2xl font-bold text-amber-600">1</span>
              <span className="text-[10px] md:text-xs text-amber-500 font-semibold">{podium[0].score}</span>
            </div>
            <p className="text-[11px] md:text-xs font-bold text-foreground-900 mt-1.5 text-center">{podium[0].name.split(' ')[0]}</p>
          </div>
        )}

        {/* 3rd */}
        {podium[2] && (
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full border-3 border-orange-300 overflow-hidden mb-2">
              <img src={podium[2].photoUrl} alt={podium[2].name} className="w-full h-full object-cover" />
            </div>
            <div className="w-20 md:w-24 h-16 md:h-20 bg-orange-100 rounded-t-xl flex flex-col items-center justify-center">
              <span className="text-lg md:text-xl font-bold text-orange-500">3</span>
              <span className="text-[9px] md:text-[10px] text-orange-400">{podium[2].score}</span>
            </div>
            <p className="text-[10px] md:text-xs font-semibold text-foreground-800 mt-1.5 text-center">{podium[2].name.split(' ')[0]}</p>
          </div>
        )}
      </div>

      {/* List 4-10 */}
      {rest.length > 0 && (
        <div className="space-y-1.5">
          {rest.map((fan) => (
            <div key={fan.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors">
              <span className="w-6 text-center text-xs font-bold text-foreground-400">{fan.rank}</span>
              <div className="w-8 h-8 rounded-full overflow-hidden shrink-0">
                <img src={fan.photoUrl} alt={fan.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground-800 truncate">{fan.name}</p>
                <p className="text-[9px] text-foreground-400">{fan.username}</p>
              </div>
              <span className="text-xs font-bold text-foreground-700">{fan.score}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}